/**
 * use-users — 用户管理 API hooks (ADM-USR-S001~S005)
 */

import type {
  AddDeviceWhitelistRequest,
  AdminUserDetailResponse,
  BannedUserListItem,
  DeviceWhitelistResponse,
  FeedbackListItem,
  UpdateUserLocaleRequest,
  UserChapterReadingRecordItem,
  UserChapterUnlockRecordItem,
  UserConsumeRecordItem,
  UserListQuery,
  UserListResponse,
  UserNovelReadingRecordItem,
  UserRechargeRecordItem,
  UsersStatsResponse,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';
import { downloadAuthedCsv } from '~/api/download-authed-csv';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export const usersApi = {
  // 用户列表 CSV 导出（裸 CSV 流，与列表同筛选）：透传 app_id/status/q/country/language/paid_status
  exportCsv: (query: {
    app_id?: string;
    status?: string;
    q?: string;
    country?: string[];
    language?: string[];
    paid_status?: 'paid' | 'unpaid';
  }) => {
    const qs = new URLSearchParams();
    if (query.app_id) qs.set('app_id', query.app_id);
    if (query.status) qs.set('status', query.status);
    if (query.q) qs.set('q', query.q);
    for (const c of query.country ?? []) qs.append('country', c);
    for (const l of query.language ?? []) qs.append('language', l);
    if (query.paid_status) qs.set('paid_status', query.paid_status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return downloadAuthedCsv(`${API_BASE || ''}/api/v1/users/export${suffix}`, 'users.csv');
  },
  stats: (appId?: string) =>
    apiRequest<UsersStatsResponse>({
      method: 'GET',
      path: '/api/v1/users/stats',
      query: appId ? { app_id: appId } : {},
    }),
  detail: (id: string) =>
    apiRequest<AdminUserDetailResponse>({ method: 'GET', path: `/api/v1/users/${id}` }),
  // 用户子资源（ADM-USR 详情抽屉记录 tab）：offset 分页，envelope 顶层 total/page/limit/hasMore
  listRecharges: (
    id: string,
    page?: number,
    limit = 50,
  ): Promise<OffsetPagedResult<UserRechargeRecordItem>> =>
    apiRequestOffsetPaged<UserRechargeRecordItem>({
      method: 'GET',
      path: `/api/v2/users/${id}/recharge-records`,
      query: { ...(page ? { page } : {}), limit },
    }),
  listConsumes: (
    id: string,
    page?: number,
    limit = 50,
  ): Promise<OffsetPagedResult<UserConsumeRecordItem>> =>
    apiRequestOffsetPaged<UserConsumeRecordItem>({
      method: 'GET',
      // 资产维度消费记录在 v2（A3 §7.2）；v1 是章节解锁消耗兼容形态，新 Web 不读
      path: `/api/v2/users/${id}/consume-records`,
      query: { ...(page ? { page } : {}), limit },
    }),
  listFeedback: (
    id: string,
    page?: number,
    limit = 50,
  ): Promise<OffsetPagedResult<FeedbackListItem>> =>
    apiRequestOffsetPaged<FeedbackListItem>({
      method: 'GET',
      path: `/api/v1/users/${id}/feedback-records`,
      query: { ...(page ? { page } : {}), limit },
    }),
  // 阅读与解锁记录子资源（A3 §7.3）：同为 offset 分页
  listChapterReadingRecords: (
    id: string,
    page?: number,
    limit = 50,
  ): Promise<OffsetPagedResult<UserChapterReadingRecordItem>> =>
    apiRequestOffsetPaged<UserChapterReadingRecordItem>({
      method: 'GET',
      path: `/api/v1/users/${id}/chapter-reading-records`,
      query: { ...(page ? { page } : {}), limit },
    }),
  listChapterUnlockRecords: (
    id: string,
    page?: number,
    limit = 50,
  ): Promise<OffsetPagedResult<UserChapterUnlockRecordItem>> =>
    apiRequestOffsetPaged<UserChapterUnlockRecordItem>({
      method: 'GET',
      path: `/api/v1/users/${id}/chapter-unlock-records`,
      query: { ...(page ? { page } : {}), limit },
    }),
  listNovelReadingRecords: (
    id: string,
    page?: number,
    limit = 50,
  ): Promise<OffsetPagedResult<UserNovelReadingRecordItem>> =>
    apiRequestOffsetPaged<UserNovelReadingRecordItem>({
      method: 'GET',
      path: `/api/v1/users/${id}/novel-reading-records`,
      query: { ...(page ? { page } : {}), limit },
    }),
  list: (query: UserListQuery) =>
    apiRequest<UserListResponse>({
      method: 'GET',
      path: '/api/v1/users',
      query,
    }),
  ban: (id: string, reason: string) =>
    apiRequest<AdminUserDetailResponse>({
      method: 'POST',
      path: `/api/v1/users/${id}/ban`,
      body: { reason },
    }),
  /** root 手动修正地区 / 语言（一次一项，后端同事务落操作日志） */
  updateLocale: (id: string, body: UpdateUserLocaleRequest) =>
    apiRequest<AdminUserDetailResponse>({
      method: 'PATCH',
      path: `/api/v1/users/${id}/locale`,
      body,
    }),
  unban: (id: string) =>
    apiRequest<AdminUserDetailResponse>({
      method: 'POST',
      path: `/api/v1/users/${id}/unban`,
    }),
  listBanned: () =>
    apiRequest<BannedUserListItem[]>({ method: 'GET', path: '/api/v1/users/banned' }),
  // 设备白名单已迁顶层 /api/v1/device-whitelist（张培 PR332，per-App key）；offset 顶层壳（A3 §9.1）。
  // per-App 有界（≤200）→ limit=200 一页取全，供弹窗 dedup / 200 上限预检；可选 ?app_id 过滤。
  listDeviceWhitelist: (appId?: string): Promise<OffsetPagedResult<DeviceWhitelistResponse>> =>
    apiRequestOffsetPaged<DeviceWhitelistResponse>({
      method: 'GET',
      path: '/api/v1/device-whitelist',
      query: { limit: 200, ...(appId ? { app_id: appId } : {}) },
    }),
  addDevice: (body: AddDeviceWhitelistRequest) =>
    apiRequest<DeviceWhitelistResponse>({
      method: 'POST',
      path: '/api/v1/device-whitelist',
      body,
    }),
  removeDevice: (id: string) =>
    apiRequest<{ ok: true }>({
      method: 'DELETE',
      path: `/api/v1/device-whitelist/${id}`,
    }),
};
